import React, { FC, useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid } from "@mui/material";
import Controls from "../../../../../atoms/Controls";
import NewFullScreenBill from "../../DirectBilling/NewBill/pages/NewFullScreenBill";
import { BillFullScreenDialog } from "./BillFullScreenDialog";
import PaperFullScreenBill from "../../PaperBilling/New/pages/PaperFullScreenBill";
import CopaymentFullScreenBill from "../../Copayment/New/pages/CopaymentFullScreen";
import NewCorrectionBill from "../../CorrectionBilling/New/pages/NewCorrectionBill";
import NewLineItemCorrectionBill from "../../LineItemCorrectionBilling/New/pages/NewLineItemCorrectionBill";
import { BillingTypeEnum } from "../types/insuranceBills.types";
import useStore from "../../../../../helpers/useStore";
import { UserContext } from "../../../../../stores/User/User.provider";
import { useUserAccounts } from "../../../../../stores/User/User.hooks";
import notificationStore from "../../../../../stores/Notification/notificationStore";
import { GkvNewBillPage } from "../../DirectBilling/GKVNew";

interface AccountSelectionDialogProps {
  paymentType: BillingTypeEnum;
  isOpen: boolean;
  onCancel: () => void;
}

const AccountSelectionDialog: FC<AccountSelectionDialogProps> = ({
  paymentType,
  isOpen,
  onCancel,
}) => {
  const UserStore = useStore(UserContext);
  const { user } = UserStore;
  const userAccounts = useUserAccounts();

  if (!user || !user.id) throw new Error("User not found");

  const [selectedAccount, setSelectedAccount] = useState<string>("");

  // Event handlers for the dropdowns
  const handleAccountChange = (event: any) => {
    if (!userAccounts) {
      notificationStore.showMessage("Mandanten noch nicht geladen", "error");
    } else {
      const selectedValue = event.target.value;
      const isValid = userAccounts.some((option: any) => option.id === selectedValue);

      if (isValid) {
        setSelectedAccount(selectedValue);
      } else {
        console.warn("Invalid selection");
      }
    }
  };

  const [isFullScreenDialogOpen, setIsFullScreenDialogOpen] = useState(false);

  // console.log("Should Full Screen Open", isFullScreenDialogOpen);

  const handleSubmit = () => {
    setIsFullScreenDialogOpen(true);
  };

  const handleFullScreenDialogClose = () => {
    setIsFullScreenDialogOpen(false);
    onCancel();
  };

  return (
    <>
      <Dialog open={isOpen} maxWidth="md" fullWidth>
        <DialogTitle sx={{ textAlign: "center" }}>{"Mandant und Kostenträger"}</DialogTitle>
        <DialogContent sx={{ marginTop: 2 }}>
          <Grid
            container
            display={"flex"}
            spacing={2}
            justifyContent={
              paymentType === BillingTypeEnum.GKV ||
              paymentType === BillingTypeEnum.PAPER ||
              paymentType === BillingTypeEnum.ZUZAHLUNG ||
              paymentType === BillingTypeEnum.CORRECTION ||
              paymentType === BillingTypeEnum.LINE_ITEM_CORRECTION
                ? "center"
                : "space-evenly"
            }
          >
            <Grid
              item
              xs={
                paymentType === BillingTypeEnum.GKV ||
                paymentType === BillingTypeEnum.PAPER ||
                paymentType === BillingTypeEnum.ZUZAHLUNG ||
                paymentType === BillingTypeEnum.CORRECTION ||
                paymentType === BillingTypeEnum.LINE_ITEM_CORRECTION
                  ? 6
                  : 4
              }
              style={{ textAlign: "center" }}
            >
              <Controls.Select
                required
                name="account"
                label="Mandant"
                id={1}
                value={selectedAccount}
                onChange={handleAccountChange}
                options={userAccounts?.map((option: any) => ({
                  label: option.id.toString(),
                  value: option.id,
                  id: option.id,
                }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancel}>Schließen</Button>
          <Button onClick={handleSubmit} color="primary" disabled={!selectedAccount}>
            Einreichen
          </Button>
        </DialogActions>
      </Dialog>

      {isFullScreenDialogOpen && paymentType === BillingTypeEnum.GKV && (
        <BillFullScreenDialog
          open={isFullScreenDialogOpen}
          fullScreen
          titleComp={"Abrechnung"}
          actions={
            <>
              <Button variant="text" onClick={handleFullScreenDialogClose}>
                Schließen
              </Button>
            </>
          }
          children={
            //<NewFullScreenBill
            <GkvNewBillPage
              handleFullScreenDialogClose={handleFullScreenDialogClose}
              selectedAccount={selectedAccount}
              userName={`${user.firstName} ${user.lastName}`}
              userId={user.id}
            />
          }
        />
      )}
      {isFullScreenDialogOpen && paymentType === BillingTypeEnum.PAPER && (
        <BillFullScreenDialog
          open={isFullScreenDialogOpen}
          fullScreen
          titleComp={"Abrechnung"}
          actions={
            <>
              <Button variant="text" onClick={handleFullScreenDialogClose}>
                Schließen
              </Button>
            </>
          }
          children={
            <PaperFullScreenBill
              handleFullScreenDialogClose={handleFullScreenDialogClose}
              selectedAccount={selectedAccount}
              userName={`${user.firstName} ${user.lastName}`}
              userId={user.id}
            />
          }
        />
      )}
      {isFullScreenDialogOpen && paymentType === BillingTypeEnum.ZUZAHLUNG && (
        <BillFullScreenDialog
          open={isFullScreenDialogOpen}
          fullScreen
          titleComp={"Abrechnung"}
          actions={
            <>
              <Button variant="text" onClick={handleFullScreenDialogClose}>
                Schließen
              </Button>
            </>
          }
          children={
            <CopaymentFullScreenBill
              handleFullScreenDialogClose={handleFullScreenDialogClose}
              selectedAccount={selectedAccount}
              userName={`${user.firstName} ${user.lastName}`}
              userId={user.id}
            />
          }
        />
      )}
      {isFullScreenDialogOpen && paymentType === BillingTypeEnum.CORRECTION && (
        <BillFullScreenDialog
          open={isFullScreenDialogOpen}
          fullScreen
          titleComp={"Korrekturen"}
          actions={
            <>
              <Button variant="text" onClick={handleFullScreenDialogClose}>
                Schließen
              </Button>
            </>
          }
          children={
            <NewCorrectionBill
              handleFullScreenDialogClose={handleFullScreenDialogClose}
              selectedAccount={selectedAccount}
              userName={`${user.firstName} ${user.lastName}`}
              userId={user.id}
            />
          }
        />
      )}
      {isFullScreenDialogOpen && paymentType === BillingTypeEnum.LINE_ITEM_CORRECTION && (
        <BillFullScreenDialog
          open={isFullScreenDialogOpen}
          fullScreen
          titleComp={"Positionskorrektur"}
          actions={
            <>
              <Button variant="text" onClick={handleFullScreenDialogClose}>
                Schließen
              </Button>
            </>
          }
          children={
            <NewLineItemCorrectionBill
              handleFullScreenDialogClose={handleFullScreenDialogClose}
              selectedAccount={selectedAccount}
              userName={`${user.firstName} ${user.lastName}`}
              userId={user.id}
            />
          }
        />
      )}
    </>
  );
};

export default AccountSelectionDialog;
